/*
 * Slides are clamped to the page-content width, so a small number of them can't
 * cover very wide viewports — Embla then silently falls back to `loop: false`.
 * Per Embla's recommended fix we duplicate slides until they fill the viewport,
 * while keeping the dots mapped to the real slide count.
 */
(function () {
  function prepareClone(clone) {
    clone.setAttribute('data-hero-clone', '');
    clone.removeAttribute('id');

    // Drop Shopify editor hooks so the theme customizer ignores the clones.
    Array.prototype.slice.call(clone.attributes).forEach(function (attr) {
      if (attr.name.indexOf('data-shopify') === 0) clone.removeAttribute(attr.name);
    });
    Array.prototype.forEach.call(clone.querySelectorAll('[id]'), function (el) {
      el.removeAttribute('id');
    });

    // Clones reuse the originals' image URLs, so they must never compete with
    // them for eager/high-priority loading.
    Array.prototype.forEach.call(clone.querySelectorAll('img'), function (img) {
      img.setAttribute('loading', 'lazy');
      img.setAttribute('fetchpriority', 'low');
    });
  }

  function fillSlidesForLoop(container, slides) {
    var originals = Array.prototype.slice.call(slides);
    var realCount = originals.length;
    if (realCount < 2) return realCount;

    var slideWidth = originals[0].getBoundingClientRect().width;
    if (!slideWidth) return realCount;

    var viewport = Math.max(window.innerWidth || 0, (window.screen && window.screen.width) || 0);
    // Centred looping needs the non-selected slides to cover the viewport, plus
    // headroom so the wrap stays glitch-free. Aim for ~1.5x the viewport width.
    var target = Math.ceil((viewport * 1.5) / slideWidth) + 1;
    if (target <= realCount) return realCount;

    var copies = Math.min(Math.ceil(target / realCount) - 1, 8);
    for (var c = 0; c < copies; c++) {
      for (var i = 0; i < realCount; i++) {
        var clone = originals[i].cloneNode(true);
        prepareClone(clone);
        container.appendChild(clone);
      }
    }

    return realCount;
  }

  function setupDots(api, dotsNode, count) {
    var labelTemplate = dotsNode.getAttribute('data-dot-label-template') || '';

    var fragment = document.createDocumentFragment();
    for (var i = 0; i < count; i++) {
      var dot = document.createElement('button');
      dot.className = 'hero-carousel__dot';
      dot.type = 'button';

      dot.setAttribute('aria-label', labelTemplate.replace('[index]', i + 1).replace('[length]', count));
      fragment.appendChild(dot);
    }
    dotsNode.replaceChildren(fragment);

    var dots = Array.prototype.slice.call(dotsNode.querySelectorAll('.hero-carousel__dot'));
    dots.forEach(function (dot, index) {
      dot.addEventListener('click', function () {
        api.scrollTo(index);
      });
      dot.addEventListener('keydown', function (event) {
        var target;
        var rtl = window.getComputedStyle(dotsNode).direction === 'rtl';
        switch (event.key) {
          case 'ArrowRight':
            target = (index + (rtl ? -1 : 1) + count) % count;
            break;
          case 'ArrowLeft':
            target = (index + (rtl ? 1 : -1) + count) % count;
            break;
          case 'Home':
            target = 0;
            break;
          case 'End':
            target = count - 1;
            break;
          default:
            return;
        }
        event.preventDefault();
        dots[target].focus();
        api.scrollTo(target);
      });
    });

    function select() {
      // Clones live after the originals, so the active real slide is the
      // selected snap wrapped back into the 0..count-1 range.
      var selected = api.selectedScrollSnap() % count;
      dots.forEach(function (dot, index) {
        var active = index === selected;
        dot.classList.toggle('is-selected', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    api.on('select', select);
    select();
  }

  function setupSlideVisibility(api) {
    var slides = api.slideNodes();

    function update() {
      var inView = api.slidesInView();
      // Embla reports an empty set until it has measured;
      // acting on it would inert the entire carousel.
      if (!inView.length) return;

      for (var i = 0; i < slides.length; i++) {
        slides[i].toggleAttribute('inert', inView.indexOf(i) === -1);
      }
    }

    api.on('slidesInView', update);
    api.on('reInit', update);
    update();
  }

  function setupAutoplay(node, api, cleanup) {
    if (node.getAttribute('data-autoplay') !== 'true') return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var delay = (parseFloat(node.getAttribute('data-autoplay-speed')) || 6) * 1000;
    var timer = null;

    function stop() {
      if (timer) {
        window.clearInterval(timer);
        timer = null;
      }
    }

    function play() {
      stop();
      timer = window.setInterval(function () {
        api.scrollNext();
      }, delay);
    }

    node.addEventListener('pointerenter', stop);
    node.addEventListener('pointerleave', play);
    node.addEventListener('focusin', stop);
    node.addEventListener('focusout', play);
    api.on('pointerDown', stop);
    api.on('pointerUp', play);

    function onVisibilityChange() {
      if (document.hidden) stop();
      else play();
    }
    document.addEventListener('visibilitychange', onVisibilityChange);

    // The listeners bound to `node` die with the section's markup, but the timer
    // and the document-level listener outlive it and would keep scrolling a
    // carousel that is no longer on the page.
    cleanup.push(function () {
      stop();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    });

    play();
  }

  // Every initialised carousel, so the theme editor can tear one down before it
  // re-renders the section over the top of it.
  var instances = [];

  function init(node) {
    if (node.hasAttribute('data-embla-init')) return;
    if (typeof window.EmblaCarousel !== 'function') return;

    var viewport = node.querySelector('.embla__viewport');
    if (!viewport) return;

    var container = viewport.querySelector('.embla__container');
    var slides = viewport.querySelectorAll('.embla__slide');
    if (!slides.length) return;

    node.setAttribute('data-embla-init', '');

    var realCount = container ? fillSlidesForLoop(container, slides) : slides.length;

    var api = window.EmblaCarousel(viewport, {
      loop: realCount > 1,
      align: 'center',
      skipSnaps: false
    });

    var prev = node.querySelector('.embla__prev');
    var next = node.querySelector('.embla__next');
    if (prev) {
      prev.addEventListener('click', function () {
        api.scrollPrev();
      });
    }
    if (next) {
      next.addEventListener('click', function () {
        api.scrollNext();
      });
    }

    var dotsNode = node.querySelector('.embla__dots');
    if (dotsNode) setupDots(api, dotsNode, realCount);

    var cleanup = [];
    setupSlideVisibility(api);
    setupAutoplay(node, api, cleanup);

    instances.push({
      node: node,
      destroy: function () {
        for (var i = 0; i < cleanup.length; i++) cleanup[i]();
        // Embla owns a ResizeObserver, an IntersectionObserver and a document
        // keydown listener of its own; only its own destroy() releases them.
        api.destroy();
        node.removeAttribute('data-embla-init');
      }
    });
  }

  function destroyWithin(root) {
    for (var i = instances.length - 1; i >= 0; i--) {
      if (root !== instances[i].node && !root.contains(instances[i].node)) continue;
      instances[i].destroy();
      instances.splice(i, 1);
    }
  }

  function initAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var nodes = scope.querySelectorAll('.hero-carousel');
    for (var i = 0; i < nodes.length; i++) init(nodes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  // Editing a section re-renders it, so without this every edit left the old
  // instance's timer and observers running against detached markup.
  document.addEventListener('shopify:section:unload', function (event) {
    destroyWithin(event.target);
  });
})();
